/**
 * The layout of a door's window (D37) as the configurator edits it: every key present, each
 * change a new object, and the request the API takes.
 *
 * The theme reads a layout (`door-window.ts`); this module changes one. The rules are the
 * server's (`schemas/door_layout.py`): the order is a permutation of the three elements, the
 * formats are limited by the presentation, the labels are 1 to 40 characters, and the slide
 * "Nochmal versuchen" leads back to is one of the door's.
 */
import { DEFAULT_DOOR_LABELS, DEFAULT_STEP_LAYOUT, DOOR_RATIOS, } from "@countdown/theme";
import { moveItem } from "./list.js";
/** `DoorLabels` on the server: 1 to 40 characters. */
export const MAX_LABEL = 40;
/** `retry_slide` on the server: 1 to the most slides a door holds. */
export const MAX_RETRY_SLIDE = 5;
/** The names of the elements, the image sides and the formats as the configurator says them. */
export const ELEMENT_LABELS = {
    image: "Bild",
    title: "Titel",
    text: "Text",
};
/** Short: they stand as segments of one strip (21.10.2026). */
export const IMAGE_SIDE_LABELS = {
    full: "Ganze Breite",
    left: "Links",
    right: "Rechts",
};
export const RATIO_LABELS = {
    "16:9": "16:9",
    "4:3": "4:3",
    "1:1": "1:1",
    auto: "Wie das Bild",
};
/** A step as the API may send it, every key filled from the defaults. */
function fullStep(step) {
    const order = step?.order;
    return {
        order: order !== undefined && isPermutation(order) ? [...order] : [...DEFAULT_STEP_LAYOUT.order],
        image_side: step?.image_side ?? DEFAULT_STEP_LAYOUT.image_side,
        ratio: step?.ratio ?? DEFAULT_STEP_LAYOUT.ratio,
    };
}
function isPermutation(order) {
    return order.length === 3 && new Set(order).size === 3;
}
/** A layout with every key set; `null` is the built-in one. */
export function fullLayout(layout) {
    return {
        slide: fullStep(layout?.slide),
        quiz: fullStep(layout?.quiz),
        prize: fullStep(layout?.prize),
        labels: { ...DEFAULT_DOOR_LABELS, ...layout?.labels },
        retry_slide: layout?.retry_slide ?? 1,
    };
}
/** The formats a presentation offers: every one on a page, the wide ones in a dialog. */
export function ratiosFor(presentation) {
    return DOOR_RATIOS[presentation];
}
/**
 * The layout with every format the presentation offers: a format it does not offer becomes
 * its first (16:9). Like `DoorLayout.fitted` on the server, so a switch to the dialog shows
 * here what the participants will see.
 */
export function fitLayout(layout, presentation) {
    const offered = ratiosFor(presentation);
    const fit = (step) => offered.includes(step.ratio) ? step : { ...step, ratio: offered[0] ?? "16:9" };
    return { ...layout, slide: fit(layout.slide), quiz: fit(layout.quiz), prize: fit(layout.prize) };
}
/** One step of the layout changed. */
export function withStep(layout, step, patch) {
    return { ...layout, [step]: { ...layout[step], ...patch } };
}
/** One element dropped onto another: it takes that one's place. */
export function droppedElement(order, source, target) {
    return moveItem(order, order.indexOf(source), order.indexOf(target));
}
/** The slide "Nochmal versuchen" can lead to, 1 to the door's slides (one at least). */
export function retrySlideIn(layout, slideCount) {
    return Math.min(Math.max(1, layout.retry_slide), Math.max(1, slideCount));
}
/**
 * The layout as the API takes it: a label left empty is the built-in one, and the retry
 * slide stays inside the server's bounds. The texts are trimmed as the server would refuse
 * a label of spaces.
 */
export function layoutRequest(layout) {
    const label = (key) => layout.labels[key].trim().slice(0, MAX_LABEL) || DEFAULT_DOOR_LABELS[key];
    const step = (shape) => ({ ...shape, order: [...shape.order] });
    return {
        slide: step(layout.slide),
        quiz: step(layout.quiz),
        prize: step(layout.prize),
        labels: { to_quiz: label("to_quiz"), submit: label("submit"), retry: label("retry") },
        retry_slide: Math.min(Math.max(1, Math.round(layout.retry_slide)), MAX_RETRY_SLIDE),
    };
}
/** Whether two layouts send the same request. */
export function sameLayout(left, right) {
    return (JSON.stringify(left === null ? null : layoutRequest(left)) ===
        JSON.stringify(right === null ? null : layoutRequest(right)));
}
//# sourceMappingURL=layout.js.map